import { useNavigate, useParams } from 'react-router-dom'
import { account } from '../../../classes/Account'
import { refreshAccessToken } from '../../../utilities/extensions'
import { useEffect, useState } from 'react'
import { apiURL } from '../../../utilities/URLs'

export default function ERCAgreementPage() {
  const navigate = useNavigate()
  const { applicationId } = useParams()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [signingUrl, setSigningUrl] = useState('')

  useEffect(() => { createSession() }, [])

  // Listen for esign_complete from the PixelInk iframe
  useEffect(() => {
    const handleMessage = async (event) => {
      // PixelInk sends { type: 'esign_complete', session_id, document_hash }
      if (event.data?.type === 'esign_complete' || event.data?.type === 'ESIGN_COMPLETE') {
        // Update application stage + GHL pipeline
        try {
          const res = await fetch(`${apiURL}/api/applications/${applicationId}/erc-agreement/`, {
            method: 'PUT',
            headers: { 'Authorization': account.accessToken }
          })
          const data = await res.json()
          if (data.response?.error) {
            if (data.response.error === 'Failed To Authenticate Token.') {
              const refreshed = await refreshAccessToken()
              if (!refreshed) return navigate('/', { state: { expired: true } })
            }
          }
        } catch (err) {
          console.error('Error completing ERC Agreement:', err)
        }
        navigate(`/applications/${applicationId}/questionnaire`)
      }
    }
    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [applicationId, navigate])

  const createSession = async () => {
    setLoading(true)
    setError('')

    try {
      const response = await fetch(`${apiURL}/api/applications/${applicationId}/erc-agreement/`, {
        headers: { 'Authorization': account.accessToken }
      })
      const data = await response.json()

      if (data.response?.error) {
        if (data.response.error === 'Failed To Authenticate Token.') {
          const refreshed = await refreshAccessToken()
          if (refreshed) return createSession()
          navigate('/', { state: { expired: true } })
          return
        }
        setError(data.response.error)
        return
      }

      // Use the PixelInk signing URL from the response
      setSigningUrl(data.response.signing_url)
    } catch (err) {
      setError('Failed to load the ERC Agreement. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className='w-full h-full flex flex-col items-center p-6 overflow-hidden bg-white dark:bg-slate-900 transition-colors duration-300'>
      <div className='w-full flex-1 flex flex-col min-h-0 p-4'>
        <div className='mb-4 shrink-0 transition-colors duration-300'>
          <h1 className='text-3xl font-bold text-gray-900 dark:text-white text-center mb-2'>ERC Agreement</h1>
          <p className='text-gray-600 dark:text-slate-400 text-center text-sm'>Please make sure to read and e-sign the following ERC Agreement.</p>
        </div>
        <div className='w-full flex-1 flex flex-col min-h-0'>
          {loading ? (
            <div className='flex flex-row items-center justify-center gap-2 py-8 transition-colors duration-300'>
              <div className='loader ease-linear rounded-full border-[6px] border-t-[6px] border-gray-200 dark:border-slate-700 border-t-ertc-primary dark:border-t-ertc-primary h-8 w-8 animate-spin'></div>
              <p className='text-slate-600 dark:text-slate-300'>Loading document...</p>
            </div>
          ) : error ? (
            <div className='flex flex-col items-center justify-center gap-4 p-8 transition-colors duration-300'>
              <div className='bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-500/20 rounded-lg p-6 max-w-md text-center'>
                <p className='text-red-600 dark:text-red-400 font-medium mb-4'>{error}</p>
                <button
                  onClick={() => { setError(''); createSession() }}
                  className='px-6 py-2 bg-ertc-primary text-white rounded-lg hover:bg-ertc-primary-dark transition-all'
                >
                  Try Again
                </button>
              </div>
            </div>
          ) : (
            <div className='w-full flex-1 min-h-0'>
              <iframe
                src={signingUrl}
                className='w-full h-full border-0 rounded-lg'
                title='ERC Agreement'
                allow='fullscreen'
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}